"use client";

import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import { hasNoSidebar } from "@/lib/sidebar-routes";
import { useSidebar } from "../ui/sidebar";

function Handle({
  className,
  style,
  onClick,
  open,
}: {
  className?: string;
  style?: React.CSSProperties;
  onClick: () => void;
  open: boolean;
}) {
  // A super-slight chevron drawn as a single rounded-join path so the bend is
  // perfectly smooth. Closed → points right (">", open me); open → points left
  // ("<", close me), flipped via scaleX. The angle is deliberately tiny so it
  // reads as a hint, not a real chevron.
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Toggle sidebar"
      className={cn(
        "group fixed top-1/2 z-30 flex -translate-y-1/2 items-center justify-center py-3 pl-2.5 pr-2",
        "focus-visible:outline-none",
        className,
      )}
      style={style}
    >
      <svg
        width="6"
        height="44"
        viewBox="0 0 6 44"
        fill="none"
        aria-hidden="true"
        className={cn(
          "overflow-visible text-gray-300 transition-[transform,color] duration-200",
          "group-hover:text-gray-400 group-focus-visible:text-gray-400",
          !open && "-scale-x-100",
        )}
      >
        {/* Two halves meeting at the vertex (2.75, 22). On hover each rotates
            around its anchored left tip so the tips stay put and the point
            swings across to the other side — the chevron flips direction
            (mirrored about the tips) without distorting the strokes. */}
        <line
          x1="1.5"
          y1="2"
          x2="2.75"
          y2="22"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          className="transition-transform duration-200 group-hover:rotate-[7deg] group-focus-visible:rotate-[7deg]"
          style={{ transformBox: "view-box", transformOrigin: "1.5px 2px" }}
        />
        <line
          x1="2.75"
          y1="22"
          x2="1.5"
          y2="42"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          className="transition-transform duration-200 group-hover:-rotate-[7deg] group-focus-visible:-rotate-[7deg]"
          style={{ transformBox: "view-box", transformOrigin: "1.5px 42px" }}
        />
      </svg>
    </button>
  );
}

export function AppSidebarTrigger() {
  const pathname = usePathname();
  const { toggleSidebar, open, openMobile } = useSidebar();

  if (hasNoSidebar(pathname)) {
    return null;
  }

  // Two handles swapped by the `md` media query rather than one handle that
  // repositions based on `isMobile`. Crossing the breakpoint becomes an instant
  // `display` swap (not animatable), so the handle never slides across the
  // screen on resize — while the desktop open/close still animates smoothly.
  return (
    <>
      {/* Mobile: parked at the left edge; opens the sheet. */}
      <Handle
        onClick={toggleSidebar}
        open={openMobile}
        className="md:hidden"
        style={{ left: "-6px" }}
      />

      {/* Desktop: rides the nav bar — left edge when closed, right edge of the
          bar when open, animating with the sidebar on toggle. */}
      <Handle
        onClick={toggleSidebar}
        open={open}
        className="hidden transition-[left] duration-200 ease-linear md:flex"
        style={{ left: open ? "calc(var(--sidebar-width) - 3px)" : "-8px" }}
      />
    </>
  );
}
